import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { DepartmentalGoals, DepartmentalGoalItem } from '../types';

export const createDepartmentalGoals = async (
  department: string,
  quarter: number,
  year: number,
  goals: DepartmentalGoalItem[],
  createdBy: string
): Promise<string> => {
  const docData: Omit<DepartmentalGoals, 'id'> = {
    department,
    quarter,
    year,
    goals,
    createdBy,
    createdAt: serverTimestamp() as Timestamp,
    updatedAt: serverTimestamp() as Timestamp,
  };

  const docRef = await addDoc(collection(db, 'departmental_goals'), docData);
  return docRef.id;
};

export const getDepartmentalGoalsById = async (id: string): Promise<DepartmentalGoals | null> => {
  try {
    const docRef = doc(db, 'departmental_goals', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as DepartmentalGoals;
    }
    return null;
  } catch (error) {
    console.error('Error getting departmental goals:', error);
    return null;
  }
};

export const getAllDepartmentalGoals = async (): Promise<DepartmentalGoals[]> => {
  try {
    const q = query(
      collection(db, 'departmental_goals'),
      orderBy('year', 'desc'),
      orderBy('quarter', 'desc'),
      orderBy('department', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as DepartmentalGoals));
  } catch (error) {
    console.error('Error getting all departmental goals:', error);
    return [];
  }
};

export const getDepartmentalGoalsByQuarter = async (
  quarter: number,
  year: number
): Promise<DepartmentalGoals[]> => {
  try {
    const q = query(
      collection(db, 'departmental_goals'),
      where('quarter', '==', quarter),
      where('year', '==', year),
      orderBy('department', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as DepartmentalGoals));
  } catch (error) {
    console.error('Error getting departmental goals by quarter:', error);
    return [];
  }
};

export const getDepartmentalGoalsByDepartment = async (
  department: string
): Promise<DepartmentalGoals[]> => {
  try {
    const q = query(
      collection(db, 'departmental_goals'),
      where('department', '==', department),
      orderBy('year', 'desc'),
      orderBy('quarter', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as DepartmentalGoals));
  } catch (error) {
    console.error('Error getting departmental goals by department:', error);
    return [];
  }
};

export const updateDepartmentalGoals = async (
  id: string,
  goals: DepartmentalGoalItem[]
): Promise<void> => {
  await updateDoc(doc(db, 'departmental_goals', id), {
    goals,
    updatedAt: serverTimestamp(),
  });
};

export const updateDepartmentalGoalsDetails = async (
  id: string,
  department: string,
  quarter: number,
  year: number,
  goals: DepartmentalGoalItem[]
): Promise<void> => {
  await updateDoc(doc(db, 'departmental_goals', id), {
    department,
    quarter,
    year,
    goals,
    updatedAt: serverTimestamp(),
  });
};

export const deleteDepartmentalGoals = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, 'departmental_goals', id));
};

export const getUniqueDepartments = async (): Promise<string[]> => {
  try {
    const snapshot = await getDocs(collection(db, 'departmental_goals'));
    const departments = new Set<string>();
    snapshot.docs.forEach(doc => {
      departments.add(doc.data().department);
    });
    return Array.from(departments).sort();
  } catch (error) {
    console.error('Error getting unique departments:', error);
    return [];
  }
};
